import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { Activity, Loader2, RefreshCw } from "lucide-react";

/**
 * DashboardActivityWidget — personal activity stream for logged-in users.
 *
 * Lives inside DashboardPage. Calls GET /api/activity/mine with the
 * user's email header. Shows:
 *   - their own scans
 *   - their own AI citation wins
 *   - their own purchases
 *   - their own win-moments
 *
 * Empty state nudges them to run their first scan.
 */
export default function DashboardActivityWidget({ userEmail }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!userEmail) return;
    try {
      const r = await axios.get(`${API}/activity/mine?limit=15`, {
        headers: { "X-User-Email": userEmail },
        withCredentials: true,
      });
      setEvents(r.data?.events || []);
    } catch (e) {
      console.error("dashboard activity load failed", e);
    }
  }, [userEmail]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  return (
    <section
      data-testid="dashboard-activity-widget"
      className="bg-white rounded-2xl border border-gray-200 p-5 lg:p-6"
    >
      <header className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <Activity className="w-5 h-5 text-purple-600" />
          Moja aktivita
        </h3>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="dashboard-activity-refresh"
          className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Obnoviť
        </button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-400">
          Zatiaľ žiadna aktivita.<br />
          Spusti svoj prvý <span className="font-semibold text-gray-700">AI Snapshot</span>{" "}
          aby sa tu začali objavovať udalosti.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {events.map((ev, i) => (
            <li
              key={i}
              data-testid={`dashboard-activity-item-${i}`}
              className="py-2.5 flex items-start gap-3"
            >
              <span className="text-xl flex-shrink-0 mt-0.5">{ev.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 truncate">
                  <span className="font-semibold">{ev.name}</span>{" "}
                  <span className="text-gray-600">{ev.message}</span>
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {ev.timestamp ? new Date(ev.timestamp).toLocaleString("sk-SK", {
                    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                  }) : ""}
                  {ev.verified && (
                    <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-bold">
                      OVERENÉ
                    </span>
                  )}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
